import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontalIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChartConfigWithPopulatedData } from '@/features/charts/api/types';
import { BaseChart } from '@/features/charts/components/base-chart';
import { EditChartDialog } from '@/features/charts/components/chart-dialog';
import { ChartTypeSelector } from '@/features/charts/components/chart-type-selector';
import {
  ChartType,
  AggregationType,
} from '@/features/charts/components/create-chart-type-selection';
import {
  ChartStats,
  CustomChartHeader,
} from '@/features/charts/components/custom-chart-header';
import { EmptyChartPlaceholder } from '@/features/charts/components/empty-chart-placeholder';
import { chartConfigsQueryKey } from '@/features/charts/hooks/use-chart-configs-for-project';
import { useDeleteChartMutation } from '@/features/charts/hooks/use-delete-chart-config';
import {
  generateChartConfigAndData,
  generateColorVariants,
  generateMockChartData,
} from '@/features/charts/util/chart-util';
import { usePartners } from '@/features/partners/hooks/use-partners';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/shared/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiType, ProjectType } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';

export const ChartFromConfig = ({
  config,
  projectType,
  dimensions,
  chartClassName,
}: {
  config: ChartConfigWithPopulatedData;
  projectType: ProjectType;
  dimensions?: {
    height?: string;
    width?: string;
    minWidth?: string;
  };
  chartClassName?: string;
}) => {
  const aggregationIntervall = 'monthly';

  const chartType = config?.chart_type as ChartType;
  const { projectId } = useParams();
  const { mutate: deleteChart } = useDeleteChartMutation();
  const queryClient = useQueryClient();
  const { data: partners = [] } = usePartners();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleDelete = () => {
    deleteChart(
      { chartId: config.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: chartConfigsQueryKey(Number(projectId)),
          });
        },
      },
    );
  };

  const { t } = useTranslation('shared');
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const colors = generateColorVariants(config.kpis.length);

  const { data: chartResult, isLoading: isChartLoading } = useQuery({
    queryKey: [
      'chartConfigAndData',
      config.id,
      config.kpis.map((k) => k.id).join(','),
      config.aggregation_type,
      aggregationIntervall,
      chartType,
      partners.length,
    ],
    queryFn: () =>
      generateChartConfigAndData({
        kpis: config.kpis,
        aggregationType: config.aggregation_type as AggregationType,
        aggregationIntervall: aggregationIntervall,
        colors: colors,
        chartType: chartType,
        partners,
      }),
  });

  const chartData = chartResult?.chartData ?? [];
  const chartConfig = chartResult?.chartConfig ?? {};

  const defaultDimensions = {
    height: 'max-h-[500px]',
    width: 'w-full',
  };

  const finalDimensions = {
    height: dimensions?.height || defaultDimensions.height,
    width: dimensions?.width || defaultDimensions.width,
    minWidth: dimensions?.minWidth,
  };

  const isEmpty = chartData.length === 0;

  const unit = config?.kpis[0]?.unit;
  const kpiType = config?.kpis[0]?.type as KpiType;
  const maxValue = config?.kpis[0]?.max_value;

  const chartTitle = config?.title ?? config?.kpis[0]?.name ?? '';
  const chartDescription = getKpiUnitTranslationPlural(tKpiTypes, unit);

  const { data: mockChartResult, isLoading: isMockLoading } = useQuery({
    queryKey: ['mockChartData', chartType, config.aggregation_type, maxValue],
    queryFn: () =>
      generateMockChartData(
        chartType,
        config.aggregation_type as AggregationType,
        maxValue ?? undefined,
      ),
  });

  const isLoading = isChartLoading || isMockLoading;

  // Only block rendering while loading - once loaded, always render
  // The chart component handles empty states internally
  const canRenderChart =
    !isLoading && (chartResult !== undefined || mockChartResult !== undefined);

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex size-9 items-center justify-center rounded-xs border border-white/30 hover:bg-white/10">
          <MoreHorizontalIcon color="white" className="size-5" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => setIsEditDialogOpen(true)}>
          {t('edit')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDelete}>
          {t('delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className={cn('relative', finalDimensions.height)}>
      <Card
        className={cn(
          `h-full ${finalDimensions.width} ${finalDimensions.minWidth}`,
        )}
        variant="chart"
      >
        <CardHeader>
          <CustomChartHeader title={chartTitle} description={chartDescription}>
            <div
              className={cn(
                'flex flex-row items-center gap-4',
                isEmpty && 'pointer-events-none opacity-30 blur-[5px]',
              )}
            >
              <ChartTypeSelector currChartType={chartType} />
              {actionsMenu}
            </div>
          </CustomChartHeader>
        </CardHeader>

        <div className={cn(isEmpty && 'opacity-50 blur-[10px]')}>
          <CardContent>
            {canRenderChart ? (
              <BaseChart
                chartType={chartType}
                chartResult={
                  isEmpty && mockChartResult
                    ? mockChartResult
                    : { chartData, chartConfig }
                }
                colors={colors}
                aggregationType={config.aggregation_type as AggregationType}
                kpiType={kpiType}
                maxValue={maxValue ?? undefined}
                chartClassName={chartClassName}
                unit={unit}
                chartSettings={{
                  hideXAxis:
                    config.aggregation_type === AggregationType.TIMELINE &&
                    chartType === ChartType.BAR
                      ? false
                      : true,
                }}
              />
            ) : (
              <div
                className={cn(
                  'flex items-center justify-center',
                  chartClassName,
                )}
              />
            )}
          </CardContent>
          <CardFooter className="flex flex-row items-center justify-center gap-4">
            <ChartStats
              chartData={chartData}
              aggregationType={config.aggregation_type as AggregationType}
              maxValue={maxValue ?? undefined}
              unit={unit}
            />
          </CardFooter>
        </div>
      </Card>

      {isEmpty && (
        <>
          <EmptyChartPlaceholder />
          <div className="absolute right-6 top-6 z-20">{actionsMenu}</div>
        </>
      )}

      <EditChartDialog
        projectId={Number(projectId)}
        projectType={projectType}
        chartConfig={config}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
    </div>
  );
};
